import React, { useState } from 'react'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { PlusIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { Box } from '@mui/material'
import Button from '../../../../../interaxDesignSystemComponents/Button'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { TeamBoxInfo } from '../TeamBoxInfo/TeamBoxInfo'
import { TeamLanguageSelector } from '../TeamLanguageSelector/TeamLanguageSelector'
import { TESTIDS } from '../../../../../config/testids'
import { Team } from '../../types'
import { ParticipantsContainerMode } from '../ParticipantsContainer/ParticipantsContainer'

export interface TeamContainerProps {
  teams: Team[]
  onAddTeam: () => void
  onRemoveTeam: (teamId: string) => void
  onRemoveParticipant: (participantId: string) => void
  onUpdateTeamLanguage?: (teamId: string, language: string) => void
  onApplyLanguageToAll?: (language: string) => void
  onDropParticipant?: (participantId: string, teamId: string) => void
  onAutoAssign?: () => void
  unassignedCount?: number
  mode: ParticipantsContainerMode
  hasValidationErrors?: boolean
  hasMissingTeamLanguages?: boolean
  className?: string
}

export const TeamContainer: React.FC<TeamContainerProps> = ({
  teams,
  onAddTeam,
  onRemoveTeam,
  onRemoveParticipant,
  onUpdateTeamLanguage,
  onApplyLanguageToAll,
  onDropParticipant,
  onAutoAssign,
  unassignedCount = 0,
  mode,
  hasValidationErrors = false,
  hasMissingTeamLanguages = false,
  className = ''
}) => {
  const { t } = useTranslation()
  const [bulkLanguage, setBulkLanguage] = useState<string | undefined>(undefined)

  // Debug logging

  // Group teams into rows of 3
  const teamsPerRow = 3
  const teamRows = []
  for (let i = 0; i < teams.length; i += teamsPerRow) {
    teamRows.push(teams.slice(i, i + teamsPerRow))
  }

  const handleApplyLanguageToAll = (language: string) => {
    onApplyLanguageToAll?.(language)
    setBulkLanguage(language)
  }

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 2,
        border: 1,
        backgroundColor: INTERAX_COLORS.heritageBlue40,
        borderColor: INTERAX_COLORS.heritageBlue,
        ...(className && { className })
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: { xs: 'stretch', lg: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 2, lg: 0 },
          mb: 3
        }}
      >
        <Box>
          <Box
            className="text-headline-4"
            sx={{
              color: INTERAX_COLORS.white,
              mb: 0.5
            }}
          >
            {t('facilitator.trainingSprint.addParticipants.teams')} ({teams.length})
          </Box>
          <Box
            className="text-body-3"
            sx={{
              color: INTERAX_COLORS.thunderGrey
            }}
          >
            {t('facilitator.trainingSprint.addParticipants.reorderTeamsDescription')}
          </Box>
        </Box>

        {/* Only show buttons in creation and edit modes */}
        {mode !== 'preview' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              gap: 2,
              width: { xs: '100%', lg: 'auto' }
            }}
          >
            {/* Auto-assign button */}
            {onAutoAssign && unassignedCount > 0 && (
              <Button
                onClick={onAutoAssign}
                variant="secondary"
                icon={<SparklesIcon className="w-5 h-5" />}
                iconPosition="left"
                data-testid={TESTIDS.SPRINT_TEAM_AUTO_ASSIGN_BTN}
              >
                {t('facilitator.trainingSprint.addParticipants.autoAssign')}
              </Button>
            )}

            {teams.length > 0 && onApplyLanguageToAll && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
                title={t('facilitator.trainingSprint.addParticipants.languageSelector.applyToAllAriaLabel')}
              >
                <TeamLanguageSelector
                  value={bulkLanguage}
                  onChange={setBulkLanguage}
                  hasError={hasMissingTeamLanguages && !bulkLanguage}
                  ariaLabel={t('facilitator.trainingSprint.addParticipants.languageSelector.applyToAllAriaLabel')}
                  placeholder={t('facilitator.trainingSprint.addParticipants.languageSelector.bulkPlaceholder')}
                  size="medium"
                  dataTestId={TESTIDS.SPRINT_TEAM_LANG_BULK_TRIGGER}
                />

                <Button
                  onClick={() => bulkLanguage && handleApplyLanguageToAll(bulkLanguage)}
                  disabled={!bulkLanguage}
                  variant="secondary"
                  size="medium"
                  data-testid={TESTIDS.SPRINT_TEAM_LANG_APPLY_ALL_BTN}
                >
                  {t('facilitator.trainingSprint.addParticipants.languageSelector.applyToAllButton')}
                </Button>
              </Box>
            )}

            {/* Add team button */}
            <Button
              onClick={onAddTeam}
              variant="primary"
              icon={<PlusIcon className="w-5 h-5" />}
              iconPosition="left"
              data-testid={TESTIDS.SPRINT_TEAM_CREATE_BTN}
            >
              {t('facilitator.trainingSprint.addParticipants.addTeam')}
            </Button>
          </Box>
        )}
      </Box>

      {hasMissingTeamLanguages && mode !== 'preview' && (
        <Box
          className="text-body-3"
          sx={{
            color: INTERAX_COLORS.redError,
            mb: 3
          }}
        >
          {t('facilitator.trainingSprint.validation.teamLanguageRequired')}
        </Box>
      )}

      {/* Teams grid */}
      {teams.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 6
          }}
        >
          <Box
            className="text-body-2"
            sx={{
              color: INTERAX_COLORS.thunderGrey
            }}
          >
            {t('facilitator.trainingSprint.addParticipants.noTeamsCreated')}
          </Box>
        </Box>
      ) : (
        <Box sx={{ '& > * + *': { mt: 3 } }}>
          {teamRows.map((rowTeams, rowIndex) => (
            <Box
              key={rowIndex}
              sx={{
                display: 'grid',
                // minmax(0, …): a plain 1fr track never shrinks below a card's longest nowrap line, so one long email pushed the row out of the section.
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  sm: 'minmax(0, 1fr)',
                  md: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(3, minmax(0, 1fr))'
                },
                gap: 2
              }}
            >
              {rowTeams.map((team) => (
                <TeamBoxInfo
                  key={team.id}
                  team={team}
                  onRemoveTeam={onRemoveTeam}
                  onRemoveParticipant={onRemoveParticipant}
                  onUpdateTeamLanguage={onUpdateTeamLanguage}
                  onDropParticipant={onDropParticipant}
                  mode={mode}
                  hasValidationErrors={hasValidationErrors}
                />
              ))}
              {/* Fill remaining slots in the last row */}
              {rowIndex === teamRows.length - 1 &&
                rowTeams.length < teamsPerRow &&
                Array.from({ length: teamsPerRow - rowTeams.length }, (_, index) => (
                  <div key={`empty-${index}`} className="hidden lg:block" />
                ))}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}
